import { AnimatePresence, motion } from "framer-motion";
import { BookDown, ChevronLeft, ChevronRight, Layers, ScrollText, Swords, X, ZoomIn } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { artAssets, officialTcgCards } from "../data/site";
import { FACTION_IDENTITIES, getCardRules, isPveOnly, KEYWORD_GLOSSARY } from "../data/tcgRules";
import { abilityOf, variantOf } from "../utils/tcg";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";

const statMeta = [
  ["scrap", "SCRAP"],
  ["atk", "ATK"],
  ["def", "DEF / HP"],
  ["surveillance", "SURVEIL"],
];

const rarityOrder = { legendary: 0, epic: 1, rare: 2, common: 3 };

const collectionFilters = [
  { id: "all", label: "ALL CARDS" },
  { id: "SCRAPPER", label: "SCRAPPER" },
  { id: "BLACK-OPS NET", label: "BLACK-OPS NET" },
  { id: "MUTANT", label: "MUTANT" },
  { id: "BOUNTY GUILD", label: "BOUNTY GUILD" },
  { id: "WASTELAND VANGUARD", label: "VANGUARD" },
  { id: "RIOTBALL", label: "AL RIOTBALL" },
  { id: "BOSS", label: "BOSS" },
  { id: "legendary", label: "LEGENDARY" },
  { id: "epic", label: "EPIC" },
];

const factionLabel = (f) => (f === "RIOTBALL" ? "AL RIOTBALL" : f);

const matchesFilter = (card, filter) => {
  if (filter === "all") return true;
  if (filter === "legendary" || filter === "epic") return card.rarity === filter;
  return card.faction === filter;
};

const PAGE_SIZE = 4;

const RarityBadge = ({ rarity }) => (
  <span className={`rarity-badge rarity-${rarity}`} data-testid={`rarity-badge-${rarity}`}>
    {rarity}
  </span>
);

const CardStats = ({ card }) => (
  <div className="vault-stats">
    {statMeta.map(([key, label]) => (
      <div key={key} data-testid={`official-card-stat-${key}`}>
        <small>{label}</small>
        <strong>{card.stats[key]}</strong>
      </div>
    ))}
  </div>
);

// core-rules chips: card type, PvE lock for the boss tier, and every keyword the
// card carries — tapping a keyword opens the rules glossary
const KeywordChips = ({ card, onOpenRules, context }) => {
  const rules = getCardRules(card);
  return (
    <div className="tcg-keyword-row" data-testid={`${context}-keyword-row`}>
      <span className="tcg-type-chip" data-testid={`${context}-type-chip`}>{rules.type}</span>
      {isPveOnly(card) && (
        <span className="tcg-pve-chip" data-testid={`${context}-pve-chip`}>PvE ONLY</span>
      )}
      {rules.keywords.map((kw) => (
        <button
          type="button"
          key={kw}
          className="tcg-keyword-chip"
          onClick={onOpenRules}
          data-testid={`${context}-keyword-${kw.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          aria-label={`${kw} — open rules glossary`}
        >
          {kw.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export function TcgSection() {
  const [cards] = useState(() => [...officialTcgCards].sort(() => Math.random() - 0.5));
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomCard, setZoomCard] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [playbookBusy, setPlaybookBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  /* the official playbook is members-only: signed in and holding 500+ PTS */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tcg/playbook/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setPlaybook(data))
      .catch(() => {});
  }, []);

  const downloadPlaybook = async () => {
    if (!playbook?.signed_in) {
      toast.error("SIGN IN TO YOUR VAULT FIRST — THE PLAYBOOK IS MEMBERS-ONLY.");
      return;
    }
    if (!playbook.unlocked) {
      toast.error(`PLAYBOOK LOCKED — YOUR STASH READS ${playbook.points}/${playbook.required} PTS.`);
      return;
    }
    setPlaybookBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tcg/playbook/file`, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "PLAYBOOK LOCKED.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Dolo-Wasteland-TCG-Official-Playbook.docx";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PLAYBOOK SECURED — CHECK YOUR DOWNLOADS.");
    } catch {
      toast.error("SIGNAL LOST — RETRY.");
    } finally {
      setPlaybookBusy(false);
    }
  };
  const [page, setPage] = useState(0);
  const activeCard = cards[activeIndex];

  const collection = useMemo(
    () =>
      [...officialTcgCards]
        .filter((card) => matchesFilter(card, filter))
        .sort(
          (a, b) =>
            (rarityOrder[a.rarity] ?? 3) - (rarityOrder[b.rarity] ?? 3) ||
            Number(b.stats.scrap) - Number(a.stats.scrap),
        ),
    [filter],
  );

  const pageCount = Math.max(1, Math.ceil(collection.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageCards = collection.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const pickFilter = (id) => {
    setFilter(id);
    setPage(0);
  };

  const moveCard = (direction) => {
    setActiveIndex((current) => (current + direction + cards.length) % cards.length);
  };

  useEffect(() => {
    if (!zoomCard && !showRules) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setZoomCard(null);
        setShowRules(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [zoomCard, showRules]);

  return (
    <section id="tcg" className="tcg-section" data-testid="tcg-section">
      <SectionHeading
        eyebrow="03 / WASTELAND TCG"
        title={`${officialTcgCards.length} official cards have breached the table.`}
        text="Six banners, one war: Scrapper rebels, the Black-Ops Net council, Mutant elites, the Bounty Guild's hunters, Wasteland Vanguard bastions, and the AL Riotball Championship itself — plus five BOSS commanders that only join your vault through weekly boss victories. Every card carries a rarity tier, scrap cost, combat stats, and a live battle ability. Browse the database — four cards a page — and inspect any card at full resolution."
      />

      <Reveal className="card-vault" delay={0.1}>
        <button
          type="button"
          className="active-card-stage"
          onClick={() => setZoomCard(activeCard)}
          data-testid="active-card-stage"
          aria-label={`Inspect ${activeCard.name} at full resolution`}
        >
          <span className="stage-zoom-hint" data-testid="stage-zoom-hint">
            <ZoomIn size={13} /> INSPECT
          </span>
          <AnimatePresence mode="wait">
            <motion.span
              key={activeCard.id}
              className="flip-card active-card-flip"
              initial={{ opacity: 0, rotateY: -180 }}
              animate={{ opacity: 1, rotateY: -4 }}
              exit={{ opacity: 0, rotateY: 180 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className={`flip-face flip-front holo-card rarity-${activeCard.rarity}`}>
                <img
                  src={activeCard.image}
                  alt={`${activeCard.name} official Wasteland TCG card`}
                  className="active-card-image"
                  data-testid="active-official-card-image"
                />
              </span>
              <span className="flip-face flip-back">
                <img src={artAssets.cardBack} alt="" aria-hidden="true" />
              </span>
            </motion.span>
          </AnimatePresence>
        </button>

        <div className="card-vault-copy" aria-live="polite" data-testid="active-official-card-detail">
          <div className="vault-meta-row">
            <span>{activeCard.cardNo} / {factionLabel(activeCard.faction)}</span>
            <RarityBadge rarity={activeCard.rarity} />
          </div>
          <h3>{activeCard.name}</h3>
          <p className="card-role">{activeCard.role}</p>
          <KeywordChips card={activeCard} onOpenRules={() => setShowRules(true)} context="active" />
          {abilityOf(activeCard) && (
            <p className="card-ability-line" data-testid="active-card-ability">
              {abilityOf(activeCard).name} — {abilityOf(activeCard).text}
            </p>
          )}
          <CardStats card={activeCard} />
          <blockquote>“{activeCard.flavor}”</blockquote>
          <div className="vault-controls">
            <button type="button" onClick={() => moveCard(-1)} data-testid="official-card-prev-button" aria-label="Previous official card">
              <ChevronLeft size={20} />
            </button>
            <span data-testid="official-card-position">{String(activeIndex + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}</span>
            <button type="button" onClick={() => moveCard(1)} data-testid="official-card-next-button" aria-label="Next official card">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </Reveal>

      <Reveal className="tcg-collection" delay={0.18}>
        <div className="tcg-collection-header">
          <h3 data-testid="tcg-collection-title">
            CARD DATABASE <span>{String(collection.length).padStart(2, "0")} / {officialTcgCards.length} UNITS</span>
          </h3>
          <div className="tcg-filter-bar" role="group" aria-label="Filter card database">
            {collectionFilters.map(({ id, label }) => (
              <button
                type="button"
                key={id}
                className={`tcg-filter-button${filter === id ? " is-active" : ""}`}
                onClick={() => pickFilter(id)}
                data-testid={`tcg-filter-${id.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                aria-pressed={filter === id}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              className="tcg-filter-button tcg-rules-button"
              onClick={() => setShowRules(true)}
              data-testid="tcg-rules-open-button"
            >
              <ScrollText size={13} /> RULES
            </button>
            <button
              type="button"
              className="tcg-filter-button tcg-playbook-button"
              onClick={downloadPlaybook}
              disabled={playbookBusy}
              data-testid="tcg-playbook-button"
              aria-label="Download the official playbook — members holding 500 PTS"
            >
              <BookDown size={13} /> PLAYBOOK · {playbook ? (playbook.unlocked ? "READY" : `${playbook.points}/${playbook.required} PTS`) : "500 PTS"}
            </button>
          </div>
        </div>

        <div className="tcg-collection-grid" data-testid="tcg-collection-grid">
          {pageCards.map((card) => (
            <button
              type="button"
              key={card.id}
              className={`tcg-collection-card rarity-${card.rarity}`}
              onClick={() => setZoomCard(card)}
              data-testid={`tcg-collection-card-${card.id}`}
              aria-label={`Inspect ${card.name}`}
            >
              <img loading="lazy" src={card.image} alt={`${card.name} TCG card`} />
              <span className="collection-name">{card.name.split(",")[0]}</span>
              <span className="collection-meta" data-testid={`collection-meta-${card.id}`}>
                {card.cardNo} · {variantOf(card) || factionLabel(card.faction)}
              </span>
              <RarityBadge rarity={card.rarity} />
            </button>
          ))}
        </div>

        <div className="tcg-pager" data-testid="tcg-pager">
          <button
            type="button"
            className="tcg-pager-button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
            data-testid="tcg-page-prev"
            aria-label="Previous card page"
          >
            <ChevronLeft size={16} /> PREV
          </button>
          <span className="tcg-pager-position" data-testid="tcg-page-position">
            PAGE {String(safePage + 1).padStart(2, "0")} / {String(pageCount).padStart(2, "0")}
          </span>
          <button
            type="button"
            className="tcg-pager-button"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={safePage >= pageCount - 1}
            data-testid="tcg-page-next"
            aria-label="Next card page"
          >
            NEXT <ChevronRight size={16} />
          </button>
        </div>
      </Reveal>

      <Reveal className="tcg-arena-cta" delay={0.22}>
        <Link to="/tcg" className="tcg-cta-primary" data-testid="tcg-build-deck-link">
          <Layers size={16} /> BUILD YOUR DECK
        </Link>
        <Link to="/tcg?mode=battle" className="tcg-cta-secondary" data-testid="tcg-enter-arena-link">
          <Swords size={16} /> ENTER THE ARENA
        </Link>
      </Reveal>

      <AnimatePresence>
        {zoomCard && (
          <motion.div
            className="tcg-zoom-overlay"
            onClick={() => setZoomCard(null)}
            data-testid="tcg-zoom-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24 }}
          >
            <motion.div
              className="tcg-zoom-modal"
              role="dialog"
              aria-modal="true"
              aria-label={`${zoomCard.name} card inspection`}
              data-testid="tcg-zoom-modal"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9, y: 26 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 18 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                type="button"
                className="tcg-zoom-close"
                onClick={() => setZoomCard(null)}
                data-testid="tcg-zoom-close-button"
                aria-label="Close card inspection"
              >
                <X size={20} />
              </button>
              <span className={`holo-card rarity-${zoomCard.rarity}`}>
                <img
                  src={zoomCard.image}
                  alt={`${zoomCard.name} official Wasteland TCG card, full resolution`}
                  className="tcg-zoom-image"
                  data-testid="tcg-zoom-image"
                />
              </span>
              <div className="card-vault-copy tcg-zoom-copy">
                <div className="vault-meta-row">
                  <span>{zoomCard.cardNo} / {factionLabel(zoomCard.faction)}</span>
                  {variantOf(zoomCard) && <em className="variant-chip" data-testid="zoom-variant-chip">{variantOf(zoomCard)} PRINTING</em>}
                  <RarityBadge rarity={zoomCard.rarity} />
                </div>
                <h3>{zoomCard.name}</h3>
                <p className="card-role">{zoomCard.role}</p>
                <KeywordChips card={zoomCard} onOpenRules={() => setShowRules(true)} context="zoom" />
                {abilityOf(zoomCard) && (
                  <p className="card-ability-line" data-testid="zoom-card-ability">
                    {abilityOf(zoomCard).name} — {abilityOf(zoomCard).text}
                  </p>
                )}
                <CardStats card={zoomCard} />
                <blockquote>“{zoomCard.flavor}”</blockquote>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRules && (
          <motion.div
            className="tcg-zoom-overlay"
            onClick={() => setShowRules(false)}
            data-testid="tcg-rules-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.24 }}
          >
            <motion.div
              className="tcg-zoom-modal tcg-rules-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Wasteland TCG core rules and keyword glossary"
              data-testid="tcg-rules-modal"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94, y: 22 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                type="button"
                className="tcg-zoom-close"
                onClick={() => setShowRules(false)}
                data-testid="tcg-rules-close-button"
                aria-label="Close rules glossary"
              >
                <X size={20} />
              </button>
              <div className="tcg-rules-copy">
                <p className="tcg-rules-eyebrow">WASTELAND TCG // CORE RULES</p>
                <h3>Seven keywords. Every faction gets a verb.</h3>
                <ul className="tcg-rules-list" data-testid="tcg-rules-keyword-list">
                  {KEYWORD_GLOSSARY.map((kw) => (
                    <li key={kw.id} data-testid={`rules-keyword-${kw.id}`}>
                      <strong>{kw.name}</strong>
                      <span>{kw.text}</span>
                    </li>
                  ))}
                </ul>
                <p className="tcg-rules-eyebrow">FACTION IDENTITIES</p>
                <ul className="tcg-rules-list tcg-rules-factions" data-testid="tcg-rules-faction-list">
                  {FACTION_IDENTITIES.map((f) => (
                    <li key={f.faction} data-testid={`rules-faction-${f.faction.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <strong>{f.faction} — {f.verb}</strong>
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
                <p className="tcg-rules-note">
                  New card types land in future print runs — TACTICS (instant tricks), GEAR (unit attachments), and more TERRAIN.
                  Rules Matches with full turn-based combat are in training; Quick Skirmish stays in the Arena.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
